import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Camera, WavyHorizone } from 'assets';
import {
  useTheme,
  useCameraAndGPSAccess,
  useDeviceModelHint,
  useIsInIframe,
  useFullScreen
} from 'hooks';
import { Lister } from 'components/partials';
import { Header, Main } from 'components/layouts';
import { PortraitLock } from 'components/containers';
import { TermsAndConditions } from './containers/terms-and-conditions.component';
import { GdprPrivacyPolicy } from './containers/gdpr-privacy-policy.component';
import { getApiBaseUrl, makePatch, useTranslate } from 'services';

import { selectSelectedWorkflow, selectUnsupportedBrowser } from 'redux/root.selectors';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';
import { selectDeviceInfo } from 'redux/session/session.selectors';
import { selectSelectedLanguage } from 'redux/internationalization/internationalization.selectors';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { ImageTypeKeys, ImageTypeNumericKey, ImageTypeSettings, IMAGE_TYPES, IWorkflow, VIN_SUB_TYPES } from 'redux/workflows/workflows';
import { IDeviceInfo } from 'redux/session/session';
import {
  startRecordingUserSession,
  storeDeviceInfo,
  storeUseNativeCamera
} from 'redux/root.actions';
import { apiRoutes } from 'utils';

const {
  workflow_selection,
  process_flow: { inspection_sticker },
} = TRANSLATIONS_VALUES_KEYS;

const Container = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 0 1.75rem;
`;

const ListerTitle = styled.h2`
  font-size: 1em;
`;

const HorizontalDivider = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 1rem auto;
`;

const ImageTypeNode = styled.div`
  font-size: 0.8rem;
  font-weight: 400;
  display: flex;
  justify-content: start;
  align-items: center;
  align-content: center;
  padding-top: 1.625rem;

  & > .num {
    font-weight: bold;
    width: 10%;
  }

  & > .img {
    width: 20%;
  }

  & > .name {
    font-weight: bold;
    width: 70%;
  }
`;

const UserConsentContainer = styled.div`
  margin-bottom: 1rem;
`;

interface StartScanningProps {}

const StartScanning : React.FC<StartScanningProps> = () => {
  const { toggleFullScreen } = useFullScreen();
  const history = useHistory();
  const {
    styles: { colorPrimaryButtonText, colorParagraph },
    styles
  } = useTheme();

  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [isUserConsentSaving, setIsUserConsentSaving] = useState(false);
  const [userConsentAccepted, setUserConsentAccepted] = useState(false);

  const workflow: IWorkflow = useSelector(selectSelectedWorkflow);
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const deviceInfo: IDeviceInfo = useSelector(selectDeviceInfo);

  const selectedLanguage = useSelector(selectSelectedLanguage);
  const { translate } = useTranslate();
  const dispatch = useDispatch();

  const currentModelHint = useDeviceModelHint();
  const isInIframe = useIsInIframe();
  const unsupportedBrowser = useSelector(selectUnsupportedBrowser);

  const { getCameraAndGPSAccess, isAccessGrantingInProgress, isAccessGranted } = useCameraAndGPSAccess();

  const startScanningNotAllowed = (photoSeries.smartScanUserConsentEnabled && !isTermsAndConditionsAccepted) ||
    isAccessGrantingInProgress ||
    isRedirecting ||
    isUserConsentSaving;

  const imageTypeSettings = workflow?.imageTypeSettings ?? photoSeries?.imageTypeSettings;
  const orderedImageTypes = _.reduce(
    _.orderBy(imageTypeSettings, ['order'], ['asc']),
    (acc: ImageTypeSettings[], imageTypeSettings) => {
      if (imageTypeSettings.enabled) {
        acc.push(imageTypeSettings);
      }
      return acc;
    },
    []
  );
  const vinIndex = imageTypeSettings && imageTypeSettings.findIndex((setting) => setting.imageType === ImageTypeKeys.Vin);
  const isInspectionSticker = imageTypeSettings[vinIndex].enabledImageSubTypes?.includes(VIN_SUB_TYPES.INSPECTION_STICKER);

  useEffect(() => {
    if (isAccessGranted) {
      setIsRedirecting(true);
      toggleFullScreen();

      if (photoSeries.requestRegistrationNo) {
        history.push('/license-plate');
      } else {
        history.push(`/capture/vehicle-type/${workflow.vehicleType}`);
      }
    }
  }, [isAccessGranted, history, toggleFullScreen, photoSeries.requestRegistrationNo, workflow.vehicleType, dispatch]);

  useEffect(() => {
    if (!recordingStarted && userConsentAccepted) {
      dispatch(startRecordingUserSession());
      setRecordingStarted(true);
    }
  }, [dispatch, recordingStarted, userConsentAccepted]);

  useEffect(() => {
    if (deviceInfo?.rawModel === currentModelHint?.rawModel || !currentModelHint?.rawModel || currentModelHint?.isLoading) return;
    // Try to read UA Model Hints via browser
    dispatch(storeDeviceInfo(currentModelHint));

    // Disable native camera on Redmi Note phones
    const allowNativeCamera = (photoSeries.smartScanNativeCameraEnabled || unsupportedBrowser) && currentModelHint.deviceNotRedmiNote;

    dispatch(storeUseNativeCamera(allowNativeCamera));
  }, [dispatch, deviceInfo, currentModelHint, photoSeries, unsupportedBrowser]);

  const handleOnCloseScanning = () => {
    window.parent.postMessage({ action: 'closeScanningClicked' }, '*');
  };

  const acceptUserConsernt = () => {
    // No need to save user consent as it is already done with captured series
    if (photoSeries.isCaptureOnlyPhotosOfAdditionalImages) {
      setUserConsentAccepted(true);
      getCameraAndGPSAccess();
      return;
    }

    const payload : {
      gdprDocumentUrl?: string;
      termsOfServiceDocumentUrl?: string;
    } = {};

    // eslint-disable-next-line max-len
    payload.gdprDocumentUrl = `${getApiBaseUrl()}languages/${selectedLanguage.id}/gdprPrivacyPolicy?fileName=${selectedLanguage.smartScanGdprPrivacyPolicyFileName}`;

    if (photoSeries.smartScanUserConsentEnabled) {
      // eslint-disable-next-line max-len
      payload.termsOfServiceDocumentUrl = `${getApiBaseUrl()}languages/${selectedLanguage.id}/termsOfService?fileName=${selectedLanguage.smartScanTermsAndConditionsFileName}`;
    }

    const url = apiRoutes.acceptUserConsent.replace('{0}', photoSeries.id);

    setIsUserConsentSaving(true);

    makePatch(url, payload).subscribe({
      next: (ajaxResponse: any) => {
        if (ajaxResponse.status === 200) {
          setUserConsentAccepted(true);
          getCameraAndGPSAccess();
        }

        setIsUserConsentSaving(false);
      },
      error: (err: any) => {
        setIsUserConsentSaving(false);
        throw err;
      }
    });
  };

  return (
    <PortraitLock>
      <Main>
        <Container className="wrapper">
          <Header />
          <Lister>
            <Lister.Header>
              <ListerTitle>
                { translate(workflow_selection.start_scanning_header) }
              </ListerTitle>
            </Lister.Header>
            <Lister.Body>
              {_.map(orderedImageTypes, (imageTypeSettings: ImageTypeSettings, i: number) => {
                const imageType: ImageTypeNumericKey = imageTypeSettings.imageType;
                const Svg = IMAGE_TYPES[imageType].icon;
                const nameId = imageType === ImageTypeKeys.Vin && isInspectionSticker
                  ? inspection_sticker.heading
                  : IMAGE_TYPES[imageType].name;

                return (
                  <ImageTypeNode key={i.toString()}>
                    <span className="num" style={{ fontWeight: 'bold' }}>
                      {i + 1}.
                    </span>
                    <span className="img">
                      <Svg stroke={colorParagraph} />
                    </span>
                    <span className="name" style={{ fontWeight: 'bold' }}>
                      {
                        imageTypeSettings.customImageTypeId !== null
                          ? translate(`${imageTypeSettings.customImageTypeId}.title_text`)
                          : translate(nameId)
                      }
                    </span>
                  </ImageTypeNode>
                );
              })}
            </Lister.Body>
            <Lister.Footer
              type="button"
              buttonText={
                <>
                  <Camera fill={colorPrimaryButtonText} />
                  { translate(workflow_selection.start_scanning_btn) }
                </>
              }
              buttonTestId="startScanningBtn"
              onClick={() => {
                if (startScanningNotAllowed) return;
                acceptUserConsernt();
              }}
              disabled={startScanningNotAllowed}
              animate
              showSecondaryButton={isInIframe}
              onSecondaryButtonClicked={handleOnCloseScanning}
              secondaryButtonText={translate(workflow_selection.close_scanning_btn)}
              secondaryButtonTestId="closeScanningBtn"
              styles={styles}
            >
              <HorizontalDivider>
                <WavyHorizone width="100%" />
              </HorizontalDivider>
              {
                photoSeries.smartScanUserConsentEnabled && (
                  <UserConsentContainer>
                    <TermsAndConditions
                      isAccessGrantingInProgress={isAccessGrantingInProgress}
                      isTermsAndConditionsAccepted={isTermsAndConditionsAccepted}
                      setIsTermsAndConditionsAccepted={setIsTermsAndConditionsAccepted}
                    />
                  </UserConsentContainer>
                )
              }
              <GdprPrivacyPolicy />
            </Lister.Footer>
          </Lister>
        </Container>
      </Main>
    </PortraitLock>
  );
};

export default StartScanning;
